import React, { ReactNode, useCallback, useMemo } from 'react'

import { UseMutationResult } from '@tanstack/react-query'
import { APIRequestState } from 'constants/API'
import Big from 'big.js'
import { IdentifiableProduct, identifiableProduct } from 'utils/serialization/productIdentifier'
import { Nullable } from 'models/helpers'
import { ProductKindDTO } from 'models/product'
import { UpsellPayment } from 'components/pages/Gallery/common/UpsellPayment'
import { ZERO_DECIMAL } from 'constants/misc'
import { logger } from 'utils/logging'
import { decimalFromBig } from 'utils/price'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'

const productCount = 1 // Defaulting to 1 for request change as it is a single purchase

/**
 * @interface Props for the AdditionalChangeUpsellPayment component.
 */
interface Props {
  /** Current selected assignmentId */
  assignmentId: string
  /** The product kind data transfer object. */
  product: ProductKindDTO
  /** Mutation result for the additional change request. */
  additionalChangeMutation?: UseMutationResult<any, any, any, any>
  /** The state of the additional change request. */
  additionalChangeRequestState: APIRequestState
  /** The price for each additional change. */
  pricePerAdditionalChange: Big
  /** Whether the additional change purchase modal is open. */
  isAdditionalChangePurchaseModalOpen: boolean
  /** Function to set the open state of the additional change purchase modal. */
  setAdditionalChangePurchaseModalOpen: (open: boolean) => void
  /** Function to submit the additional change request. */
  submitAdditionalChangeRequest: (paymentIntentId?: string, billingProduct?: IdentifiableProduct) => void,
  /** Text to be displayed in a default success element in case of beforeConfirmAction success when stripe is not used */
  successTitle?: string,
  /** Additional content to render within the modal. */
  children?: ReactNode
}

/**
 * @component
 * AdditionalChangeUpsellPayment displays the payment modal for purchasing additional change requests as an upsell within the client gallery.
 * 
 * @example
 * <AdditionalChangeUpsellPayment
 *  assignmentId="12345"
 *  product={productData} 
 *  additionalChangeRequest={productRequestData}
 *  additionalChangeRequestState={APIRequestState.OK}
 *  pricePerAdditionalChange={new Big(10)}
 *  isAdditionalChangePurchaseModalOpen={isModalOpen}
 *  setAdditionalChangePurchaseModalOpen={setModalOpen}
 *  submitAdditionalChangeRequest={handleSubmitAdditionalChangeRequest}
 * />
 */
export const AdditionalChangeUpsellPayment: React.FC<Props> = ({
  assignmentId,
  product,
  additionalChangeMutation,
  additionalChangeRequestState,
  pricePerAdditionalChange,
  isAdditionalChangePurchaseModalOpen,
  setAdditionalChangePurchaseModalOpen,
  submitAdditionalChangeRequest,
  successTitle,
  children,
}) => {
  const { t } = useTranslation(['gallery', 'gallery_staging_flow'])

  const { spawnSuccessToast, spawnErrorToast } = useSnackbar()
  const currency = useMemo(() => product?.feePrice.currency, [product?.feePrice.currency])

  const handleOnModalClose = () => {
    setAdditionalChangePurchaseModalOpen(false)
  }

  /**
   * Submits the paid correction, identifying the picked payment option by its Product2 code — the
   * only form the correction wire accepts. The payment intent already exists here, so an option
   * without a code aborts loudly rather than sending a request the API would reject.
   */
  const handleConfirmPurchase = useCallback((paymentIntentId: string | undefined, billingProductCode: Nullable<string>) => {
    const billingProduct = identifiableProduct(billingProductCode)

    if (!billingProduct) {
      spawnErrorToast(t('gallery_staging_flow:upsell_abort_error'))
      logger.error('Additional change request not submitted - the billing product carries no product code', undefined, {
        context: { component: 'AdditionalChangeUpsellPayment', action: 'handleConfirmPurchase', metadata: { assignmentId } },
      })

      return
    }

    submitAdditionalChangeRequest(paymentIntentId, billingProduct)
  }, [assignmentId, spawnErrorToast, submitAdditionalChangeRequest, t])

  return (
    <UpsellPayment
      isOpen={isAdditionalChangePurchaseModalOpen}
      assignmentId={assignmentId}
      productId={product?.id}
      productQuantity={productCount}
      total={
        !!currency
          ? {
            value: decimalFromBig(pricePerAdditionalChange),
            currency,
            discountPercentage: product?.feePrice.discountPercentage || ZERO_DECIMAL,
          }
          : null
      }
      beforeConfirmAction={handleConfirmPurchase}
      beforeConfirmActionState={additionalChangeRequestState}
      beforeConfirmActionMutation={additionalChangeMutation}
      onClose={handleOnModalClose}
      afterClosed={handleOnModalClose}
      successTitle={successTitle}
      onSuccess={() => {
        spawnSuccessToast(t('client_gallery.documents_upsell.success_text'))
        handleOnModalClose()
      }}
      doStripRedirect={false}
    >
      {children}
    </UpsellPayment>
  )
}
